import { useCallback, useMemo } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import {
  DivIcon,
  DomEvent,
  LeafletEvent,
  Marker as LeafletMarker,
} from 'leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTheme } from '../../hooks/useTheme';
import { PlannerWaypoint } from '../../types/missionPlanner';
import {
  moveWaypoint,
  removeWaypoint,
} from '../../store/slices/missionPlannerSlice';
import { formatLatLng } from '../../utils/formatters';

/** Position of a waypoint in the route, driving its colour code. */
export type WaypointVariant = 'start' | 'mid' | 'end';

interface WaypointMarkerProps {
  waypoint: PlannerWaypoint;
  variant: WaypointVariant;
  /**
   * True when this waypoint falls outside the drone's inclusion geofence and
   * the server will refuse to save the draft (#451).
   */
  outsideGeofence?: boolean;
}

// CVD-safe, muted "same-tone" cool ramp (issue #341): blue → indigo → wine.
// Deliberately no red/green pairing (the classic deuteranopia/protanopia
// failure); the number always carries the sequence, so colour is secondary.
// White-on-fill contrast: blue 5.2:1, indigo 12:1, wine 8.7:1 — all ≥ AA.
const VARIANT_COLOR: Record<WaypointVariant, string> = {
  start: '#0072b2',
  mid: '#332288',
  end: '#882255',
};

// Geofence violation (#451). Vermillion rather than pure red, matching the
// out-of-range control points in ProfileChart. Colour is never the only signal:
// the marker also gains a warning badge, a dashed ring, and says so in its
// accessible name and tooltip — per the colour-alone rule in ui/CLAUDE.md.
const VIOLATION_COLOR = '#d55e00';

/**
 * A single numbered, draggable planner waypoint.
 *
 * Mirrors the HomeBaseMarker idiom (DivIcon + Tooltip) but is draggable: the
 * operator repositions the point directly on the map and the route polyline
 * follows via the updated slice state. The number (never colour alone) makes
 * sequence legible in greyscale and for colour-blind operators.
 */
export const WaypointMarker: React.FC<WaypointMarkerProps> = ({
  waypoint,
  variant,
  outsideGeofence = false,
}) => {
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  // Highlight this marker when its list row is hovered/focused (#295).
  const isHovered = useSelector(
    (state: RootState) => state.missionPlanner.hoveredWaypointId === waypoint.id
  );

  // Push the marker's current position into the draft. Shared by `drag` and
  // `dragend` so the live preview and the committed value can never diverge.
  const commitPosition = useCallback(
    (e: LeafletEvent) => {
      const { lat, lng } = (e.target as LeafletMarker).getLatLng();
      dispatch(moveWaypoint({ id: waypoint.id, lat, lng }));
    },
    [dispatch, waypoint.id]
  );

  const labelBg = isDarkMode ? '#2d2d2d' : '#ffffff';
  const bgColor = outsideGeofence ? VIOLATION_COLOR : VARIANT_COLOR[variant];
  const roleLabel =
    variant === 'start' ? 'Start' : variant === 'end' ? 'End' : 'Waypoint';

  // Memoised so the icon isn't rebuilt on every unrelated render (drag jank).
  const icon = useMemo(() => {
    const size = isHovered ? 38 : 30;
    const border = outsideGeofence
      ? `2.5px dashed ${labelBg}`
      : isHovered
        ? `2.5px solid #ffab00`
        : `1.5px solid ${labelBg}`;
    const shadow = isHovered
      ? '0 0 0 3px rgba(255,171,0,0.5), 0 2px 6px rgba(0,0,0,0.5)'
      : '0 2px 6px rgba(0,0,0,0.4)';
    return new DivIcon({
      html: `
          <div
            role="img"
            aria-label="${roleLabel} waypoint ${waypoint.order}${
              outsideGeofence ? ', outside the geofence' : ''
            }"
            title="${roleLabel} — waypoint ${waypoint.order}${
              outsideGeofence ? ' — outside the geofence' : ''
            }"
            style="
              background: ${bgColor};
              color: #ffffff;
              border: ${border};
              border-radius: 50%;
              width: ${size}px;
              height: ${size}px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 13px;
              font-weight: bold;
              box-shadow: ${shadow};
            "
          >${waypoint.order}</div>
          ${
            outsideGeofence
              ? `<div aria-hidden="true" style="
                   position: absolute;
                   top: -4px;
                   right: -4px;
                   background: ${labelBg};
                   color: ${VIOLATION_COLOR};
                   border-radius: 50%;
                   width: 14px;
                   height: 14px;
                   display: flex;
                   align-items: center;
                   justify-content: center;
                   font-size: 11px;
                   font-weight: bold;
                   line-height: 1;
                 ">!</div>`
              : ''
          }
        `,
      className: 'planner-waypoint-marker',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }, [waypoint.order, labelBg, bgColor, roleLabel, isHovered, outsideGeofence]);

  return (
    <Marker
      position={[waypoint.lat, waypoint.lng]}
      icon={icon}
      draggable
      autoPan
      eventHandlers={{
        // Stream the position while the marker is held so the legs, their
        // distance labels and the RTH path follow it instead of snapping into
        // place on release (#431). The slice coalesces this stream under one
        // key (`mw:<id>`), so a whole drag is still a single undo entry.
        drag: commitPosition,
        dragend: commitPosition,
        // Double-click a marker to delete it. Stop the DOM event so it never
        // reaches the map's double-click-zoom handler (#345).
        dblclick(e) {
          if (e.originalEvent) DomEvent.stop(e.originalEvent);
          dispatch(removeWaypoint(waypoint.id));
        },
      }}
    >
      <Tooltip direction="top" offset={[0, -16]}>
        <div>
          <strong>
            {roleLabel} — Waypoint {waypoint.order}
          </strong>
          <br />
          {formatLatLng(waypoint.lat, waypoint.lng)}
          {outsideGeofence && (
            <>
              <br />
              <strong>Outside the geofence — this draft cannot be saved.</strong>
            </>
          )}
        </div>
      </Tooltip>
    </Marker>
  );
};
